import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import './SentenceTemplate.css';
import { validateContentSafety } from '../utils/contentSafety';

/**
 * SentenceTemplate Component
 *
 * Implements the vertical stacked field interface for GenFlappy.
 * Users edit three fields to customize their game: Character, Obstacle, and Background.
 *
 * Key Features:
 * - Vertical stacked layout with clear label + input hierarchy
 * - Character counters (20 char limit)
 * - Grayscale theme consistency
 * - Touch-first design (44x44px minimum touch targets)
 * - Full keyboard navigation and screen reader support
 * - Pulse animation on load for visual editability cue
 *
 * Stories: 1.1 (original), 1.7 (redesigned layout)
 *
 * @param {SentenceTemplateValues} [values] - Optional controlled state values (for Stories 1.2-1.3)
 * @param {Function} [onChange] - Optional change handler (for Stories 1.2-1.3)
 *
 * @example
 * // Uncontrolled (Story 1.1 - current usage)
 * <SentenceTemplate />
 *
 * @example
 * // Controlled (Stories 1.2-1.3 - future usage)
 * const [values, setValues] = useState({ character: '', obstacle: '', background: '' });
 * <SentenceTemplate values={values} onChange={setValues} />
 */

interface SentenceTemplateValues {
  character: string;
  obstacle: string;
  background: string;
}

interface ValidationState {
  characterValid: boolean;
  obstacleValid: boolean;
  backgroundValid: boolean;
  characterError: string;
  obstacleError: string;
  backgroundError: string;
}

interface SentenceTemplateProps {
  /** Controlled state values (optional - for Stories 1.2-1.3) */
  values?: SentenceTemplateValues;
  /** Change handler for controlled mode (optional - for Stories 1.2-1.3) */
  onChange?: (values: SentenceTemplateValues) => void;
  /** Validation state (optional - for Story 1.2) */
  validationState?: ValidationState;
  /** Validation change handler (optional - for Story 1.2) */
  onValidationChange?: (state: ValidationState) => void;
}

export interface SentenceTemplateRef {
  /** Force validation on all fields and focus first invalid field */
  validateAndFocus: () => boolean;
}

const SentenceTemplate = forwardRef<SentenceTemplateRef, SentenceTemplateProps>(({
  values: controlledValues,
  onChange: controlledOnChange,
  validationState: controlledValidationState,
  onValidationChange: controlledOnValidationChange
}, ref) => {
  // State for field values (used only in uncontrolled mode)
  const [internalValues, setInternalValues] = useState<SentenceTemplateValues>({
    character: '',
    obstacle: '',
    background: '',
  });

  // State for validation (used only in uncontrolled mode)
  const [internalValidationState, setInternalValidationState] = useState<ValidationState>({
    characterValid: false, // Default false - empty is invalid per Decision 1
    obstacleValid: false,
    backgroundValid: false,
    characterError: '',
    obstacleError: '',
    backgroundError: '',
  });

  // State for tracking first blur per field
  const [hasBlurred, setHasBlurred] = useState({
    character: false,
    obstacle: false,
    background: false,
  });

  // Refs for focus management
  const characterFieldRef = useRef<HTMLInputElement>(null);
  const obstacleFieldRef = useRef<HTMLInputElement>(null);
  const backgroundFieldRef = useRef<HTMLInputElement>(null);

  // Use controlled values if provided, otherwise use internal state
  const values = controlledValues ?? internalValues;
  const setValues = controlledOnChange ?? setInternalValues;

  const validationState = controlledValidationState ?? internalValidationState;
  const setValidationState = controlledOnValidationChange ?? setInternalValidationState;

  // State for pulse animation (runs once on mount)
  const [pulseActive, setPulseActive] = useState(true);

  // Remove pulse animation after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setPulseActive(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    validateAndFocus: (): boolean => {
      // Force validation check on all fields
      const isCharValid = validateField(values.character);
      const isObsValid = validateField(values.obstacle);
      const isBgValid = validateField(values.background);

      // Update validation state
      setValidationState({
        characterValid: isCharValid,
        obstacleValid: isObsValid,
        backgroundValid: isBgValid,
        characterError: getErrorMessage(values.character),
        obstacleError: getErrorMessage(values.obstacle),
        backgroundError: getErrorMessage(values.background),
      });

      // Mark all fields as blurred to show errors
      setHasBlurred({
        character: true,
        obstacle: true,
        background: true,
      });

      // Focus first invalid field
      if (!isCharValid) {
        characterFieldRef.current?.focus();
        characterFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      if (!isObsValid) {
        obstacleFieldRef.current?.focus();
        obstacleFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      if (!isBgValid) {
        backgroundFieldRef.current?.focus();
        backgroundFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }

      return true; // All fields valid
    },
  }));

  /**
   * Pass-through function - validation now handled by validation layer
   * This allows validation errors to be shown for >20 chars and special characters
   */
  const validateInput = (value: string): string => {
    // Strip special characters (allow only letters, numbers, and spaces)
    const sanitized = value.replace(/[^a-zA-Z0-9 ]/g, '');
    // Enforce 20 character limit (for programmatic changes and paste events)
    // maxLength attribute handles normal typing, but we need this for fireEvent.change in tests
    return sanitized.slice(0, 20);
  };

  /**
   * Validates a field value and returns true if valid
   * Decision 1: Empty fields are INVALID (stricter validation)
   * Story 4.4: Added content safety as LAST check (performance optimization)
   */
  const validateField = (value: string): boolean => {
    if (value.length === 0) return false; // 1. Empty = invalid
    if (value.length > 20) return false;   // 2. Length check
    if (!/^[a-zA-Z0-9\s]*$/.test(value)) return false; // 3. Alphanumeric check

    // 4. Content safety check (LAST for performance - only runs if other checks pass)
    const safetyCheck = validateContentSafety(value);
    if (!safetyCheck.isValid) return false;

    return true;
  };

  /**
   * Gets error message for invalid field value
   * Story 4.4: Added content safety error message
   */
  const getErrorMessage = (value: string): string => {
    if (value.length === 0) return 'This field is required';
    if (value.length > 20) return 'Maximum 20 characters allowed';
    if (!/^[a-zA-Z0-9\s]*$/.test(value)) return 'Only letters, numbers, and spaces allowed';

    // Content safety error (NEW in Story 4.4)
    const safetyCheck = validateContentSafety(value);
    if (!safetyCheck.isValid) return 'Please use family-friendly words';

    return '';
  };

  /**
   * Logs content safety violations (Story 4.4)
   * Helper to avoid code duplication between handleBlur and handleChange
   */
  const logContentSafetyBlock = (field: 'character' | 'obstacle' | 'background', value: string): void => {
    // Only log if other validations pass (not empty, valid length, valid characters)
    if (value.length > 0 && value.length <= 20 && /^[a-zA-Z0-9\s]*$/.test(value)) {
      const safetyCheck = validateContentSafety(value);
      if (!safetyCheck.isValid) {
        console.warn('Content safety blocked', {
          field,
          reason: safetyCheck.reason,
          timestamp: Date.now(),
          // NO user input logged (privacy protection)
        });
      }
    }
  };

  /**
   * Handles blur event to track first blur and trigger validation
   * Story 4.4: Added content safety logging
   */
  const handleBlur = (field: 'character' | 'obstacle' | 'background') => {
    setHasBlurred(prev => ({ ...prev, [field]: true }));

    // Validate immediately on first blur
    const value = values[field];
    const isValid = validateField(value);
    const error = isValid ? '' : getErrorMessage(value);

    // Story 4.4: Log blocked attempts (extracted to helper)
    logContentSafetyBlock(field, value);

    setValidationState(prev => ({
      ...prev,
      [`${field}Valid`]: isValid,
      [`${field}Error`]: error,
    }));
  };

  /**
   * Handles input changes with validation
   * Validates in real-time only after first blur
   * Story 4.4: Added content safety logging
   */
  const handleChange = (field: keyof SentenceTemplateValues, value: string) => {
    const validValue = validateInput(value);
    setValues(prev => ({ ...prev, [field]: validValue }));

    // Validate only if field has been blurred
    if (hasBlurred[field]) {
      const isValid = validateField(validValue);
      const error = isValid ? '' : getErrorMessage(validValue);

      // Story 4.4: Log blocked attempts (extracted to helper)
      logContentSafetyBlock(field, validValue);

      setValidationState(prev => ({
        ...prev,
        [`${field}Valid`]: isValid,
        [`${field}Error`]: error,
      }));
    }
  };

  /**
   * Gets character counter color based on length
   */
  const getCounterColor = (length: number): string => {
    if (length >= 20) return 'error';
    if (length >= 15) return 'warning';
    return 'normal';
  };

  // Helper to get validation class for a field
  const getValidationClass = (field: 'character' | 'obstacle' | 'background'): string => {
    if (!hasBlurred[field]) return '';
    return validationState[`${field}Valid`] ? 'valid' : 'error';
  };

  return (
    <div className="sentence-template-container">
      <div className="field-group">
        <label htmlFor="character-field" className="field-label">A</label>
        <div className="input-wrapper">
          <input
            id="character-field"
            ref={characterFieldRef}
            type="text"
            className={`editable-field ${pulseActive ? 'pulse' : ''} ${getValidationClass('character')}`}
            value={values.character}
            onChange={(e) => handleChange('character', e.target.value)}
            onBlur={() => handleBlur('character')}
            placeholder="dragon"
            aria-label="Character field"
            aria-invalid={hasBlurred.character && !validationState.characterValid}
            aria-describedby={hasBlurred.character && !validationState.characterValid ? 'character-error' : undefined}
            role="textbox"
            tabIndex={0}
            maxLength={20}
          />
          <span className={`character-counter ${getCounterColor(values.character.length)}`}>
            {values.character.length}/20
          </span>
        </div>
        {hasBlurred.character && !validationState.characterValid && (
          <div
            id="character-error"
            className="error-message"
            role="alert"
            aria-live="polite"
          >
            <span className="error-icon" aria-hidden="true">⚠</span>
            <span className="error-text">{validationState.characterError}</span>
          </div>
        )}
      </div>

      <div className="field-group">
        <label htmlFor="obstacle-field" className="field-label">Jumping over</label>
        <div className="input-wrapper">
          <input
            id="obstacle-field"
            ref={obstacleFieldRef}
            type="text"
            className={`editable-field ${pulseActive ? 'pulse' : ''} ${getValidationClass('obstacle')}`}
            value={values.obstacle}
            onChange={(e) => handleChange('obstacle', e.target.value)}
            onBlur={() => handleBlur('obstacle')}
            placeholder="volcano"
            aria-label="Obstacle field"
            aria-invalid={hasBlurred.obstacle && !validationState.obstacleValid}
            aria-describedby={hasBlurred.obstacle && !validationState.obstacleValid ? 'obstacle-error' : undefined}
            role="textbox"
            tabIndex={0}
            maxLength={20}
          />
          <span className={`character-counter ${getCounterColor(values.obstacle.length)}`}>
            {values.obstacle.length}/20
          </span>
        </div>
        {hasBlurred.obstacle && !validationState.obstacleValid && (
          <div
            id="obstacle-error"
            className="error-message"
            role="alert"
            aria-live="polite"
          >
            <span className="error-icon" aria-hidden="true">⚠</span>
            <span className="error-text">{validationState.obstacleError}</span>
          </div>
        )}
      </div>

      <div className="field-group">
        <label htmlFor="background-field" className="field-label">With background</label>
        <div className="input-wrapper">
          <input
            id="background-field"
            ref={backgroundFieldRef}
            type="text"
            className={`editable-field ${pulseActive ? 'pulse' : ''} ${getValidationClass('background')}`}
            value={values.background}
            onChange={(e) => handleChange('background', e.target.value)}
            onBlur={() => handleBlur('background')}
            placeholder="forest"
            aria-label="Background field"
            aria-invalid={hasBlurred.background && !validationState.backgroundValid}
            aria-describedby={hasBlurred.background && !validationState.backgroundValid ? 'background-error' : undefined}
            role="textbox"
            tabIndex={0}
            maxLength={20}
          />
          <span className={`character-counter ${getCounterColor(values.background.length)}`}>
            {values.background.length}/20
          </span>
        </div>
        {hasBlurred.background && !validationState.backgroundValid && (
          <div
            id="background-error"
            className="error-message"
            role="alert"
            aria-live="polite"
          >
            <span className="error-icon" aria-hidden="true">⚠</span>
            <span className="error-text">{validationState.backgroundError}</span>
          </div>
        )}
      </div>
    </div>
  );
});

SentenceTemplate.displayName = 'SentenceTemplate';

export default SentenceTemplate;
export type { SentenceTemplateValues, ValidationState, SentenceTemplateRef };
